/*@flow*/
import React from 'react';
import history from 'lib/history';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import fetchData from './fetchData';
import same from 'lib/same';
import pick from 'lib/pick';
import { camelize } from 'humps';
import {
  type HOC,
  compose,
  withStateHandlers,
  withHandlers,
  lifecycle,
} from 'recompose';
import checkboxTableHOC from './CheckboxHOC';
import BulkActions from '../BulkActions';
import api from 'lib/api';
import NonEmptyArray from 'lib/NonEmptyArray';
import pluralize from 'pluralize';

const CheckboxTable = checkboxTableHOC(ReactTable);

type SortedT = { id: string, desc: 'true' | 'false' };
type FilteredT = { id: string, value: string };

interface tableInterface {
  getWrappedInstance(): {
    getResolvedState(): {
      sortedData: Object[],
    },
  };
}

const toggleSelection = ({
  update,
  selection,
}: {
  selection: string[],
  update: Function,
}) => (key: string) => {
  const keyIndex = selection.indexOf(key);
  selection = [...selection];
  // check to see if the key exists
  if (keyIndex >= 0) {
    // it does exist so we will remove it using destructing
    selection = [
      ...selection.slice(0, keyIndex),
      ...selection.slice(keyIndex + 1),
    ];
  } else {
    // it does not exist so add it
    selection.push(key);
  }

  // update the state
  update({ selection });
};

const onSelectAll = ({
  update,
  selectTable,
}: {
  update: Function,
  selectTable: ?tableInterface,
}) => {
  if (!selectTable) return;
  const selection = [];
  // we need to get at the internals of ReactTable
  const wrappedInstance = selectTable.getWrappedInstance();
  // the 'sortedData' property contains the currently accessible records based on the filter and sort
  const currentRecords = wrappedInstance.getResolvedState().sortedData;
  // we just push all the IDs onto the selection array
  currentRecords.forEach(item => {
    if (item._original) {
      selection.push(item._original.id);
    }
  });
  update({ selectAll: true, selection });
};

const unSelectAll = ({ update }: { update: Function }) => {
  update({ selectAll: false, selection: [] });
};

const toggleAll = (props: {
  update: Function,
  selectAll: boolean,
  selectTable: ?tableInterface,
}) => () => {
  if (!props.selectAll) {
    onSelectAll(props);
  } else {
    unSelectAll(props);
  }
};

const sendPayload = (props, resourceName) => {
  const resources = pluralize(resourceName);
  return api.fetchJson(`/tables/${resources}/destroy`, {
    method: 'DELETE',
    body: props,
  });
};

const trackProgress = (jobId: string, resourceName: string) => {
  const resources = pluralize(resourceName);
  return api.fetchJson(`/tables/${resources}/destroy_progress`, {
    query: { job_id: jobId },
  });
};

const isSelected = (props: { selection: string[] }) => (key: string) =>
  props.selection.includes(key);

type DataT = any;

type InputPropsT = {|
  resourceName: string,
  keys: string[],
  labels: string[],
|};
type EnhancedPropsT = {|
  ...InputPropsT,
  loading: boolean,
  numPages: number,
  numRecords: number,
  data: DataT[],
  selection: string[],
  selectAll: boolean,
  selectTable: ?tableInterface,
  data: Object[],
  update: Function,
  onPageChange: Function,
  onSortedChange: Function,
  onPageSizeChange: Function,
  onFilteredChange: Function,
  filtered: FilteredT[],
  sorted: SortedT[],
  per: number,
  page: number,
|};

const Enhancer: HOC<EnhancedPropsT, InputPropsT> = compose(
  withStateHandlers(
    () => {
      const { page, per, filtered = [], sorted = [] } = history.getQuery();
      return {
        loading: false,
        numPages: -1,
        data: [],
        selection: [],
        selectAll: false,
        selectTable: undefined,
        filtered,
        sorted,
        per,
        page,
      };
    },
    {
      update: () => s => s,
      onPageChange: () => page => ({ page }),
      onSortedChange: () => sorted => ({ sorted }),
      onPageSizeChange: () => pageSize => ({ per: pageSize }),
      onFilteredChange: () => filtered => ({ filtered }),
    }
  ),
  withHandlers({
    toggleSelection,
    toggleAll,
    isSelected,
  }),
  lifecycle({
    componentDidMount() {
      // Fetch data if the 'filtered' key is different than the one in local state.
      const unlistener = history.listen(loc => {
        const { filtered = [] } = loc.query;
        const didChange = !same(filtered, this.props.filtered);
        const shouldUpdate = NonEmptyArray.is(filtered) && didChange;
        if (shouldUpdate) {
          this.props.update({ filtered });
        }
      });
      this.setState({ unlistener });
    },
  }),
  lifecycle({
    componentWillUnmount() {
      this.props.unlistener();
    },
  })
);

const Component = (props: EnhancedPropsT) => {
  const { data, selection } = props;

  // TODO: determine the name of the id column
  // TODO: Ensure the id column does not have a filter input field
  const columns = props.keys.map((k, i) => ({
    Header: props.labels[i],
    accessor: camelize(k),
    minWidth: 150,
  }));

  const fns = ['selectAll', 'isSelected', 'toggleAll', 'toggleSelection'];
  const extraProps = pick(fns, props);

  return (
    <React.Fragment>
      <BulkActions
        key={props.resourceName}
        onSelectAll={() => {
          onSelectAll({
            update: props.update,
            selectTable: props.selectTable,
          });
        }}
        numRecords={props.numRecords}
        onClear={() => {
          unSelectAll({ update: props.update });
        }}
        allSelected={props.selectAll}
        selection={selection}
        onDestroy={() =>
          sendPayload(
            {
              selectAll: props.selectAll,
              selectedIds: selection,
              filtered: props.filtered,
            },
            props.resourceName
          )
        }
        onDestroyProgress={jobId => trackProgress(jobId, props.resourceName)}
      />
      <CheckboxTable
        style={{ height: '700px' }}
        columns={columns}
        manual
        filterable
        loading={props.loading}
        data={data}
        pages={props.numPages}
        page={props.page}
        onPageChange={props.onPageChange}
        sorted={props.sorted}
        onSortedChange={props.onSortedChange}
        pageSize={props.per}
        onPageSizeChange={props.onPageSizeChange}
        filtered={props.filtered}
        onFilteredChange={props.onFilteredChange}
        onFetchData={fetchData(props)}
        className="-striped -highlight"
        keyField="id"
        ref={r => {
          if (r) {
            props.update({ selectTable: r });
          }
        }}
        {...extraProps}
      />
    </React.Fragment>
  );
};
export default Enhancer(Component);
